import React, { useMemo, useState } from "react";
import styled from "styled-components";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { useEarningsBarChart } from "features/affiliation/common/hooks";
import BarsChart, { BarPoint } from "features/affiliation/NewLook/BarsChart";
import { ChartNote } from "features/affiliation/NewLook/ReferredUsersPanel";
import { FigureLarge, Meta } from "features/affiliation/NewLook/roles";
import { formatUsd } from "features/affiliation/NewLook/newLookUtils";
import { formatDateLabel } from "@shared/utils/date";

/**
 * Daily earnings of one month: the month's total as the headline (it
 * follows the crosshair to a day), quiet bars, and a month stepper in the
 * header.
 */
const EarningsByMonthPanel = ({
  chartHeight = 160,
}: {
  chartHeight?: number;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { earningsChartQuery, onDataPickerChange } = useEarningsBarChart();
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf("month"));
  const [hovered, setHovered] = useState<BarPoint>();
  const isCurrentMonth = month.isSame(dayjs(), "month");
  const monthLabel = formatDateLabel(month, "MMMM YYYY");

  const points = useMemo<BarPoint[]>(
    () =>
      (earningsChartQuery.data || []).map(([time, value]) => ({
        time: Number(time),
        value: Number(value) || 0,
      })),
    [earningsChartQuery.data]
  );
  const total = points.reduce((sum, point) => sum + point.value, 0);
  const hasEarnings = points.some((point) => point.value !== 0);
  const loading = earningsChartQuery.isFetching;

  const step = (months: number) => {
    const next = month.add(months, "month");
    setHovered(undefined);
    setMonth(next);
    onDataPickerChange?.(next, next.format("YYYY-MM"));
  };

  return (
    <Panel
      title={t("earnings.by_month")}
      actions={
        <Stepper role="group" aria-label={t("earnings.by_month")}>
          <StepButton
            type="button"
            aria-label={t("earnings.previous_month")}
            onClick={() => step(-1)}
          >
            <ChevronLeft size={16} strokeWidth={1.75} />
          </StepButton>
          <MonthLabel aria-live="polite">
            {formatDateLabel(month, "MMM YYYY")}
          </MonthLabel>
          <StepButton
            type="button"
            aria-label={t("earnings.next_month")}
            disabled={isCurrentMonth}
            onClick={() => step(1)}
          >
            <ChevronRight size={16} strokeWidth={1.75} />
          </StepButton>
        </Stepper>
      }
      data-testid="affiliation-earnings-by-month"
    >
      {/* An empty month says so once, in the chart's place. */}
      {(loading || hasEarnings) && (
        <Headline>
          {loading ? (
            <Skeleton width={96} height={22} style={{ margin: "2px 0" }} />
          ) : (
            <FigureLarge>
              {formatUsd(hovered ? hovered.value : total)}
            </FigureLarge>
          )}
          <Meta>
            {hovered
              ? t("earnings.earned_on_day", {
                  day: formatDateLabel(moment.unix(hovered.time), "MMM D"),
                })
              : t("earnings.earned_in_month", { month: monthLabel })}
          </Meta>
        </Headline>
      )}
      {loading ? (
        <Skeleton width="100%" height={chartHeight} radius={8} />
      ) : hasEarnings ? (
        <BarsChart
          points={points}
          height={chartHeight}
          formatAxisValue={(value) => `$${Math.round(value)}`}
          formatTick={(time) => formatDateLabel(moment.unix(time), "MMM D")}
          formatActiveTime={(time) =>
            formatDateLabel(moment.unix(time), "ddd, MMM D")
          }
          onActiveChange={setHovered}
          ariaLabel={t("earnings.month_chart_label", { month: monthLabel })}
        />
      ) : (
        <ChartNote style={{ height: chartHeight }}>
          {t("earnings.no_earnings_in_month", { month: monthLabel })}
        </ChartNote>
      )}
    </Panel>
  );
};

export default EarningsByMonthPanel;

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;
const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const StepButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.control - 2}px;
  background: transparent;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  transition: color 120ms, background-color 120ms;
  &:hover:not(:disabled) {
    color: ${(p) => getNewLookTokens(p.theme).ink};
    background: ${(p) => getNewLookTokens(p.theme).panel};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;
const MonthLabel = styled.span`
  min-width: 72px;
  text-align: center;
  ${newLookType.label};
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
